class BackInStock extends HTMLElement {
    constructor() {
      super();
      this._form = null;
      this._onSubmitHandler = this.onSubmit.bind(this);
      this._onInputHandler = this.onInput.bind(this);
      this._recaptchaSiteKey = this.getAttribute('data-recaptcha-site-key');
      this._klaviyoCompanyId = this.getAttribute('data-klaviyo-company-id') || 'JkKgYN';
      this._klaviyoSubscriptionListId = (this.getAttribute('data-klaviyo-subscription-list-id') || '').trim();
      this._recaptchaResponse = null;
      this._isRecaptchaInitializing = false;
    }
  
    connectedCallback() {
      this.setupListeners();
      
      // Set up reCAPTCHA callbacks if enabled
      if (this._recaptchaSiteKey && this._recaptchaSiteKey.trim() !== '') {
        // Initialize reCAPTCHA when ready
        this.waitForRecaptcha();
      } else {
        console.log('reCAPTCHA not enabled - site key not found or empty');
      }
    }
  
    waitForRecaptcha() {
      // Check if reCAPTCHA is already loaded
      if (typeof grecaptcha !== 'undefined' && grecaptcha.ready) {
        grecaptcha.ready(() => {
          this.initRecaptcha();
        });
        return;
      }
      
      // Set up global onload callback if not already set
      if (!window.onRecaptchaLoad) {
        window.onRecaptchaLoad = () => {
          // Initialize all back-in-stock instances that need reCAPTCHA
          const instances = document.querySelectorAll('back-in-stock[data-recaptcha-site-key]');
          instances.forEach(el => {
            const customElement = el;
            if (customElement && typeof grecaptcha !== 'undefined' && grecaptcha.ready) {
              grecaptcha.ready(() => {
                if (customElement.initRecaptcha) {
                  customElement.initRecaptcha();
                }
              });
            }
          });
        };
      }
      
      // Also check periodically in case onload already fired or script loads differently
      const checkRecaptcha = setInterval(() => {
        if (typeof grecaptcha !== 'undefined') {
          if (grecaptcha.ready) {
            clearInterval(checkRecaptcha);
            grecaptcha.ready(() => {
              this.initRecaptcha();
            });
          } else {
            // If grecaptcha exists but ready is not available, try direct render
            clearInterval(checkRecaptcha);
            setTimeout(() => {
              this.initRecaptcha();
            }, 500);
          }
        }
      }, 100);
      
      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkRecaptcha);
      }, 10000);
    }
  
    initRecaptcha() {
      // Prevent double initialization
      if (this._recaptchaWidgetId !== null && this._recaptchaWidgetId !== undefined) {
        return;
      }

      // Avoid race conditions from multiple async initialization paths.
      if (this._isRecaptchaInitializing) {
        return;
      }
      
      const recaptchaWidget = this.querySelector('.g-recaptcha');
      if (!recaptchaWidget) {
        console.warn('reCAPTCHA widget container not found');
        return;
      }
      
      if (typeof grecaptcha === 'undefined') {
        console.warn('grecaptcha not defined');
        return;
      }
      
      if (!this._recaptchaSiteKey || this._recaptchaSiteKey.trim() === '') {
        console.warn('reCAPTCHA site key is missing');
        return;
      }

      const existingWidgetId = recaptchaWidget.dataset.recaptchaWidgetId;
      if (existingWidgetId !== undefined && existingWidgetId !== '') {
        this._recaptchaWidgetId = Number(existingWidgetId);
        return;
      }

      // If Google already injected iframe markup, this container is already rendered.
      if (recaptchaWidget.children.length > 0) {
        console.warn('reCAPTCHA container already contains rendered markup');
        return;
      }

      this._isRecaptchaInitializing = true;
      
      // Generate unique callback names for this instance
      const instanceId = Math.random().toString(36).substr(2, 9);
      this._instanceId = instanceId;
      
      // Set up global callbacks
      window[`onRecaptchaCallback_${instanceId}`] = (response) => {
        this._recaptchaResponse = response;
        const submitButton = this._form.querySelector('[type="submit"]');
        if (submitButton) {
          submitButton.disabled = false;
        }
      };
      
      window[`onRecaptchaExpired_${instanceId}`] = () => {
        this._recaptchaResponse = null;
        const submitButton = this._form.querySelector('[type="submit"]');
        if (submitButton) {
          submitButton.disabled = true;
        }
      };
      
      // Render reCAPTCHA with callbacks
      try {
        const widgetId = grecaptcha.render(recaptchaWidget, {
          'sitekey': this._recaptchaSiteKey,
          'callback': window[`onRecaptchaCallback_${instanceId}`],
          'expired-callback': window[`onRecaptchaExpired_${instanceId}`]
        });
        this._recaptchaWidgetId = widgetId;
        recaptchaWidget.dataset.recaptchaWidgetId = String(widgetId);
        console.log('reCAPTCHA rendered successfully');
      } catch (error) {
        console.error('Error rendering reCAPTCHA:', error);
      } finally {
        this._isRecaptchaInitializing = false;
      }
    }
  
    setupListeners() {
      // Remove previous listeners if any
      if (this._form) {
        this._form.removeEventListener('submit', this._onSubmitHandler);
        this._form.removeEventListener('input', this._onInputHandler);
      }
  
      // Select the form inside the custom element
      this._form = this.querySelector('form');
      if (this._form) {
        this._form.addEventListener('submit', this._onSubmitHandler);
        this._form.addEventListener('input', this._onInputHandler);
      }
    }

    onInput() {
      this.clearValidationErrors();
      this.updateSubmitState();
    }

    updateSubmitState() {
      if (!this._form) return;
      const submitButton = this._form.querySelector('[type="submit"]');
      if (!submitButton) return;

      if (!this._recaptchaSiteKey) {
        submitButton.disabled = false;
        return;
      }

      submitButton.disabled = !this._recaptchaResponse;
    }

    getEmailInput() {
      return this._form?.querySelector('[data-el="back-in-stock.input"]');
    }

    clearValidationErrors() {
      if (!this._form) return;

      const existingErrors = this._form.querySelector('.prd-BackInStock_Errors');
      if (existingErrors) existingErrors.remove();

      const emailInput = this.getEmailInput();
      if (emailInput) {
        emailInput.removeAttribute('aria-invalid');
      }
    }

    showValidationErrors(messages) {
      if (!this._form || !messages.length) return;

      const errorsWrapper = document.createElement('div');
      errorsWrapper.className = 'prd-BackInStock_Errors';
      errorsWrapper.setAttribute('role', 'alert');

      messages.forEach((message) => {
        const row = document.createElement('p');
        row.className = 'prd-BackInStock_Error';
        row.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 1.5C6.71442 1.5 5.45772 1.88122 4.3888 2.59545C3.31988 3.30968 2.48676 4.32484 1.99479 5.51256C1.50282 6.70028 1.37409 8.00721 1.6249 9.26809C1.8757 10.529 2.49477 11.6872 3.40381 12.5962C4.31285 13.5052 5.47104 14.1243 6.73192 14.3751C7.99279 14.6259 9.29973 14.4972 10.4874 14.0052C11.6752 13.5132 12.6903 12.6801 13.4046 11.6112C14.1188 10.5423 14.5 9.28558 14.5 8C14.4982 6.27665 13.8128 4.62441 12.5942 3.40582C11.3756 2.18722 9.72335 1.50182 8 1.5ZM8 13.5C6.91221 13.5 5.84884 13.1774 4.94437 12.5731C4.0399 11.9687 3.33495 11.1098 2.91867 10.1048C2.50238 9.09977 2.39347 7.9939 2.60568 6.927C2.8179 5.86011 3.34173 4.8801 4.11092 4.11091C4.8801 3.34172 5.86011 2.8179 6.92701 2.60568C7.9939 2.39346 9.09977 2.50238 10.1048 2.91866C11.1098 3.33494 11.9687 4.03989 12.5731 4.94436C13.1774 5.84883 13.5 6.9122 13.5 8C13.4983 9.45818 12.9184 10.8562 11.8873 11.8873C10.8562 12.9184 9.45819 13.4983 8 13.5ZM7.5 8.5V5C7.5 4.86739 7.55268 4.74021 7.64645 4.64645C7.74022 4.55268 7.86739 4.5 8 4.5C8.13261 4.5 8.25979 4.55268 8.35356 4.64645C8.44732 4.74021 8.5 4.86739 8.5 5V8.5C8.5 8.63261 8.44732 8.75979 8.35356 8.85355C8.25979 8.94732 8.13261 9 8 9C7.86739 9 7.74022 8.94732 7.64645 8.85355C7.55268 8.75979 7.5 8.63261 7.5 8.5ZM8.75 10.75C8.75 10.8983 8.70602 11.0433 8.6236 11.1667C8.54119 11.29 8.42406 11.3861 8.28701 11.4429C8.14997 11.4997 7.99917 11.5145 7.85368 11.4856C7.7082 11.4566 7.57456 11.3852 7.46967 11.2803C7.36478 11.1754 7.29335 11.0418 7.26441 10.8963C7.23548 10.7508 7.25033 10.6 7.30709 10.463C7.36386 10.3259 7.45999 10.2088 7.58333 10.1264C7.70666 10.044 7.85167 10 8 10C8.19892 10 8.38968 10.079 8.53033 10.2197C8.67098 10.3603 8.75 10.5511 8.75 10.75Z" fill="#A81818"/></svg><span>' +
          message +
          '</span>';
        errorsWrapper.appendChild(row);
      });

      this._form.prepend(errorsWrapper);
    }

    validateForm() {
      const messages = [];
      const emailInput = this.getEmailInput();
      const email = emailInput?.value?.trim() || '';
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!email) {
        messages.push('Please enter your email address');
      }

      if (!emailRegex.test(email)) {
        messages.push('Please enter a valid email address');
      }

      if (this._recaptchaSiteKey && !this._recaptchaResponse) {
        messages.push('Please complete the reCAPTCHA verification.');
      }

      if (emailInput) {
        if (messages.length) {
          emailInput.setAttribute('aria-invalid', 'true');
        } else {
          emailInput.removeAttribute('aria-invalid');
        }
      }

      return messages;
    }
  
    async onSubmit(e) {
      e.preventDefault();

      this.clearValidationErrors();
      const validationMessages = this.validateForm();
      if (validationMessages.length) {
        this.showValidationErrors(validationMessages);
        return;
      }
  
      const submitButton = this._form.querySelector('[type="submit"]');
      if (submitButton) {
        submitButton.disabled = true;
      }
  
      try {
        const response = await this.subscribeToBIS();
        const email = this._form.querySelector('[data-el="back-in-stock.input"]')?.value?.trim();

        if (response.ok && email && this._klaviyoSubscriptionListId) {
          try {
            await this.subscribeToEmailMarketing(email);
          } catch (marketingError) {
            console.warn('Back-in-stock saved, but email marketing subscribe failed:', marketingError);
          }
        } else if (response.ok && email && !this._klaviyoSubscriptionListId) {
          console.warn(
            'Back-in-stock saved. Email marketing subscribe skipped: set Klaviyo subscription list ID in theme settings (required by Klaviyo client/subscriptions).'
          );
        }
  
        const messageDir = this.getAttribute('data-klaviyo-flow-branch') === 'lighting-notification'
          ? 'lightingNotification'
          : 'backInStock';
        const messages = window.theme.strings[messageDir] || {};
        const message = messages[response.ok ? 'success' : 'failure'] || 'An error occurred.';
        const cls = `prd-BackInStock_Message-${response.ok ? 'success' : 'error'}`;
  
        const messageEl = document.createElement('div');
        messageEl.className = `prd-BackInStock_Message ${cls}`;
        messageEl.innerHTML = message;
  
        this._form.style.display = 'none';
        this.appendChild(messageEl);
        
        // Reset reCAPTCHA if enabled
        if (this._recaptchaSiteKey && this._recaptchaWidgetId && typeof grecaptcha !== 'undefined') {
          grecaptcha.reset(this._recaptchaWidgetId);
          this._recaptchaResponse = null;
        }
      } catch (error) {
        console.error('Failed to subscribe:', error);
  
        const errorMessage = 'There was a problem submitting your request.';
        this.showValidationErrors([errorMessage]);
      } finally {
        this.updateSubmitState();
      }
    }
  
    async subscribeToBIS() {
      const inputEl = this._form.querySelector('[data-el="back-in-stock.input"]').value
      const variantEl = this._form.querySelector('[data-el="back-in-stock.sku"]').value

      const payload = {
        "data": {
          "type": "back-in-stock-subscription",
          "attributes": {
            "profile": {
              "data": {
                  "type": "profile",
                  "attributes": {
                      "email": `${inputEl}`,
                  }
              }
            },
            "channels": ["EMAIL"],
          },
          "relationships": {
            "variant": {
              "data": {
                "type": "catalog-variant",
                "id": `$shopify:::$default:::${variantEl}`
              }
            }
          }
        }
      }

      const url = `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${encodeURIComponent(this._klaviyoCompanyId)}`;
  
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          revision: '2024-10-15',
          'content-type': 'application/json',
          Authorization: 'Klaviyo-API-Key pk_3ed6a2ecfcd0618d7774d52208bce50217'
        },
        body: JSON.stringify(payload),
      });
  
      if (!response.ok) {
        throw new Error('Network response was not ok.'); 
      }
  
      return response;
    }

    async subscribeToEmailMarketing(email) {
      const url = `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this._klaviyoCompanyId)}`;
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'PDP Back In Stock',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  subscriptions: {
                    email: {
                      marketing: {
                        consent: 'SUBSCRIBED'
                      }
                    }
                  }
                }
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: this._klaviyoSubscriptionListId
              }
            }
          }
        }
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          revision: '2026-01-15',
          accept: 'application/vnd.api+json',
          'content-type': 'application/vnd.api+json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let errorMessage = 'Email marketing subscription failed.';

        try {
          const errorData = await response.json();
          if (errorData?.errors?.length) {
            errorMessage = errorData.errors.map((err) => err.detail || err.title).filter(Boolean).join(' | ');
          }
        } catch (_error) {
          // Keep generic error when response is not JSON.
        }

        if (/list not found/i.test(errorMessage)) {
          errorMessage +=
            ' The list must belong to the same Klaviyo account as Theme settings → Klaviyo Company ID (public Site ID from that account’s API keys).';
        }

        throw new Error(errorMessage);
      }

      return response;
    }
  }
  
  customElements.define('back-in-stock', BackInStock);
  